import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Paper,
  Chip,
} from "@mui/material";

import SubscriptionsRounded from "@mui/icons-material/SubscriptionsRounded";
import SubscriptionsRoundedIcon from "@mui/icons-material/SubscriptionsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import { usePromotions } from "../../context/promotionsContext/promotionsContext";
import { useStyle } from "../../context/styleContext/styleContext";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import ProductsPageCard from "../../components/ProductCard/ProductsPageCard";
import SaleHeroBanner from "../../components/SaleHeroBanner/SaleHeroBanner.jsx";
import Seo from "../../components/Seo";

function getProductKey(product, index) {
  return product?.SKU || product?.productURI || product?._id || index;
}

function formatPromotionEndDate(endDate) {
  if (!endDate) {
    return "";
  }

  const parsedDate = new Date(endDate);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(parsedDate);
}

function CompactMobileSaleBar({ promotion, style }) {
  if (!promotion) {
    return null;
  }

  const discountPercent = Number(promotion.discountPercent || 0);
  const endDateText = formatPromotionEndDate(promotion.endDate);

  return (
    <Box
      sx={{
        px: 1.25,
        py: 1,
        borderRadius: "12px",
        backgroundColor: style.darkPanel,
        border: "1px solid rgba(255,204,77,0.28)",
        borderLeft: "4px solid #ffcc4d",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Typography
          sx={{
            minWidth: 0,
            color: style.color4,
            fontWeight: 850,
            fontSize: "0.84rem",
            lineHeight: 1.25,
          }}
        >
          Summer Sale ·{" "}
          <Box component="span" sx={{ color: "#ffcc4d" }}>
            {discountPercent}% off every plugin
          </Box>
        </Typography>

        {endDateText && (
          <Typography
            sx={{
              flexShrink: 0,
              color: style.color3,
              fontSize: "0.74rem",
              whiteSpace: "nowrap",
            }}
          >
            Ends {endDateText}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function AllAccessBanner({ style }) {
  return (
   
<Paper
              elevation={0}
              sx={{
                width: "100%",
                minWidth: 0,
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.5, sm: 2, md: 2.5 },
                  py: { xs: 1.5, md: 2 },
                  backgroundColor: style.darkPanel,
                  borderBottom: `1px solid rgba(255,255,255,0.08)`,
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Chip
                      label="Subscription"
                      sx={{
                        mb: 1,
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.06)",
                      }}
                    />
                    <Typography
                      sx={{
                        color: style.color4,
                        fontWeight: 800,
                        fontSize: { xs: "1.35rem", md: "1.8rem" },
                        lineHeight: 1.08,
                      }}
                    >
                      Full Access Pass - Get everything with one subscription
                    </Typography>
                    <Typography
                      sx={{
                        color: style.color3,
                        lineHeight: 1.7,
                        mt: 0.9,
                        maxWidth: 760,
                      }}
                    >
                      Get full access to all plugins with a 7-day free trial, then just $9.95 per month. Cancel anytime.
                    </Typography>
                  </Box>

                  <Stack
                    direction={{ xs: "column", sm: "row", md: "column" }}
                    spacing={1}
                    sx={{ width: { xs: "100%", md: "auto" } }}
                  >
                    <Button
                      component={RouterLink}
                      to="/subscribe"
                      startIcon={<SubscriptionsRoundedIcon />}
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: style.accentGreen,
                        minWidth: { md: 220 },
                        "&:hover": {
                          backgroundColor: style.accentGreen,
                          filter: "brightness(1.05)",
                        },
                      }}
                    >
                      Learn More
                    </Button>

              
                  </Stack>
                </Stack>
              </Box>
            </Paper>
  );
}

function ProductListSchemaMarkup({ products }) {
  useEffect(() => {
    if (!products?.length) {
      return undefined;
    }

    const schemaData = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Parish Audio Plugins",
      url: "https://parishaudio.com/products",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: products.map((product, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://parishaudio.com/products/${product.productURI}`,
          name: product.name,
        })),
      },
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-parish-products-schema", "true");
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [products]);

  return null;
}

export default function ProductsPage() {
  const { style } = useStyle();
  const { activePromotions, getProductDisplayPricing } = usePromotions();

  const [dataForAllProducts, setDataForAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("featured");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    let isMounted = true;

    const getProductInfo = async () => {
      try {
        setLoading(true);
        setLoadError("");

        const { data } = await axios.post(
          "/api/get-products-available-on-website"
        );

        if (isMounted) {
          setDataForAllProducts(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Error retrieving products:", error);

        if (isMounted) {
          setLoadError(
            "Products could not be loaded. Please refresh the page and try again."
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    getProductInfo();

    return () => {
      isMounted = false;
    };
  }, []);

  const sourceOrder = useMemo(() => {
    return new Map(
      dataForAllProducts.map((product, index) => [product, index])
    );
  }, [dataForAllProducts]);

  const salePromotion = useMemo(() => {
    return activePromotions
      .filter(
        (promotion) =>
          promotion?.promoType === "percentage_off" &&
          Number(promotion?.discountPercent || 0) > 0 &&
          promotion?.scopeType === "global"
      )
      .sort(
        (a, b) =>
          Number(b.discountPercent || 0) -
          Number(a.discountPercent || 0)
      )[0];
  }, [activePromotions]);

  const getDisplayPrice = useCallback(
    (product) => {
      if (Number(product?.price || 0) <= 0) {
        return 0;
      }

      const pricing = getProductDisplayPricing(product);
      return Number(pricing?.finalPrice ?? product.price ?? 0);
    },
    [getProductDisplayPricing]
  );

  const filteredProducts = useMemo(() => {
    let products = [...dataForAllProducts];

    const query = searchTerm.trim().toLowerCase();

    if (query) {
      products = products.filter((product) => {
        const searchableText = [
          product.name,
          product.shortDescription,
          product.cardSummary,
          product.longDescription,
          product.category,
          product.catalogCategory,
          product.productCategory,
          product.productType,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(query);
      });
    }

    products = [...products];

    if (sortBy === "name-asc") {
      products.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "name-desc") {
      products.sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortBy === "price-low") {
      products.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
    } else if (sortBy === "price-high") {
      products.sort((a, b) => getDisplayPrice(b) - getDisplayPrice(a));
    } else if (sortBy === "rating") {
      products.sort((a, b) => {
        const aRating = Number(a.starRating || 0);
        const bRating = Number(b.starRating || 0);
        const aCount = Number(a.ratingCount || 0);
        const bCount = Number(b.ratingCount || 0);

        if (bRating !== aRating) {
          return bRating - aRating;
        }

        return bCount - aCount;
      });
    } else {
      products.sort((a, b) => {
        const aFeaturedOrder = Number(a.featuredOrder);
        const bFeaturedOrder = Number(b.featuredOrder);

        const aHasFeaturedOrder = Number.isFinite(aFeaturedOrder);
        const bHasFeaturedOrder = Number.isFinite(bFeaturedOrder);

        if (aHasFeaturedOrder && bHasFeaturedOrder) {
          return aFeaturedOrder - bFeaturedOrder;
        }

        if (aHasFeaturedOrder) {
          return -1;
        }

        if (bHasFeaturedOrder) {
          return 1;
        }

        if (a.productURI === "d1-delay" && b.productURI !== "d1-delay") {
          return -1;
        }

        if (b.productURI === "d1-delay" && a.productURI !== "d1-delay") {
          return 1;
        }

        return (
          Number(sourceOrder.get(a) || 0) -
          Number(sourceOrder.get(b) || 0)
        );
      });
    }

    return products;
  }, [
    dataForAllProducts,
    getDisplayPrice,
    searchTerm,
    sortBy,
    sourceOrder,
  ]);

  return (
    <>
      <Seo
        title="Plugins | Parish Audio"
        description="Browse Parish Audio plugins for vocals, guitars, dynamics, space, modulation, and music production."
        canonical="https://parishaudio.com/products"
        image="https://parishaudio.com/assets/HeroBanner1.png"
        imageAlt="Parish Audio plugins"
        ogType="website"
      />

      {dataForAllProducts.length > 0 && (
        <ProductListSchemaMarkup products={dataForAllProducts} />
      )}

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "#000",
          px: { xs: 1.1, sm: 1.5, md: 2.5 },
          py: { xs: 1.1, md: 2 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1280px",
            mx: "auto",
            minWidth: 0,
          }}
        >
          <Stack spacing={{ xs: 1.35, md: 1.7 }}>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <SaleHeroBanner />
            </Box>

            <Box sx={{ display: { xs: "block", sm: "none" } }}>
              <CompactMobileSaleBar
                promotion={salePromotion}
                style={style}
              />
            </Box>

            <Box
              component="section"
              aria-labelledby="products-heading"
              sx={{
                p: { xs: 1.25, sm: 1.5, md: 1.65 },
                borderRadius: "16px",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                boxSizing: "border-box",
              }}
            >
              <Stack spacing={1.25}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={{ xs: 1.15, md: 2 }}
                  alignItems={{ xs: "stretch", md: "center" }}
                  justifyContent="space-between"
                >
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ minWidth: 0 }}
                  >
                    <Typography
                      id="products-heading"
                      component="h1"
                      sx={{
                        color: style.color4,
                        fontWeight: 850,
                        fontSize: { xs: "1.55rem", sm: "1.75rem", md: "2rem" },
                        lineHeight: 1.05,
                        letterSpacing: "-0.025em",
                      }}
                    >
                      Plugins
                    </Typography>

                    <Typography
                      sx={{
                        color: style.color3,
                        fontSize: "0.82rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {loading
                        ? "Loading..."
                        : `${filteredProducts.length} plugin${filteredProducts.length === 1 ? "" : "s"}`}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={0.8}
                    sx={{
                      width: { xs: "100%", md: "min(640px, 53%)" },
                      minWidth: 0,
                    }}
                  >
                    <TextField
                      fullWidth
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Search plugins..."
                      variant="outlined"
                      size="small"
                      inputProps={{
                        "aria-label": "Search plugins",
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon
                              sx={{
                                color: style.color3,
                                fontSize: "1.25rem",
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        minWidth: 0,
                        "& .MuiOutlinedInput-root": {
                          color: style.color4,
                          backgroundColor: style.darkPanel,
                          borderRadius: "9px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.12)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.24)",
                        },
                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: style.accentGreen,
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: style.color3,
                          opacity: 1,
                        },
                      }}
                    />

                    <FormControl
                      size="small"
                      sx={{
                        width: { xs: 160, sm: 180 },
                        flexShrink: 0,
                        "& .MuiOutlinedInput-root": {
                          color: style.color4,
                          backgroundColor: style.darkPanel,
                          borderRadius: "9px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.12)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.24)",
                        },
                        "& .MuiSvgIcon-root": {
                          color: style.color4,
                        },
                      }}
                    >
                      <Select
                        value={sortBy}
                        onChange={(event) => setSortBy(event.target.value)}
                        displayEmpty
                        inputProps={{ "aria-label": "Sort plugins" }}
                        startAdornment={
                          <InputAdornment position="start">
                            <TuneRoundedIcon
                              sx={{
                                color: style.color3,
                                fontSize: "1.15rem",
                              }}
                            />
                          </InputAdornment>
                        }
                        renderValue={(value) => {
                          const labels = {
                            featured: "Featured",
                            rating: "Top Rated",
                            "name-asc": "A–Z",
                            "name-desc": "Z–A",
                            "price-low": "Price ↑",
                            "price-high": "Price ↓",
                          };

                          return labels[value] || "Sort";
                        }}
                      >
                        <MenuItem value="featured">Featured</MenuItem>
                        <MenuItem value="rating">Highest Rated</MenuItem>
                        <MenuItem value="name-asc">Name: A to Z</MenuItem>
                        <MenuItem value="name-desc">Name: Z to A</MenuItem>
                        <MenuItem value="price-low">
                          Price: Low to High
                        </MenuItem>
                        <MenuItem value="price-high">
                          Price: High to Low
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>

              </Stack>
            </Box>
            <br />
            <Box component="section" aria-live="polite">
              {loading ? (
                <Box sx={{ py: 8 }}>
                  <LoadingSpinner />
                </Box>
              ) : loadError ? (
                <Box
                  sx={{
                    py: 7,
                    px: 2,
                    textAlign: "center",
                    color: style.color4,
                  }}
                >
                  <Typography sx={{ fontWeight: 850, mb: 0.75 }}>
                    Products unavailable
                  </Typography>
                  <Typography sx={{ color: style.color3 }}>
                    {loadError}
                  </Typography>
                </Box>
              ) : filteredProducts.length === 0 ? (
                <Box
                  sx={{
                    py: 7,
                    px: 2,
                    textAlign: "center",
                    color: style.color4,
                  }}
                >
                  <Typography sx={{ fontWeight: 850, mb: 0.75 }}>
                    No plugins found
                  </Typography>
                  <Typography sx={{ color: style.color3 }}>
                    Try a different search term.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(3, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: { xs: 1.2, sm: 1.5, md: 1.7 },
                    alignItems: "stretch",
                  }}
                >
                  {filteredProducts.map((product, index) => (
                    <Box
                      key={getProductKey(product, index)}
                      sx={{
                        minWidth: 0,
                        width: "100%",
                        display: "flex",
                      }}
                    >
                      <ProductsPageCard productData={product} />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
              <br />
            <AllAccessBanner style={style} />
          </Stack>
        </Box>
      </Box>
    </>
  );
}


// import React, { useState, useEffect, useMemo } from "react";
// import axios from "axios";
// import {
//   Box,
//   Typography,
//   useMediaQuery,
//   TextField,
//   InputAdornment,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   Stack,
//   Paper,
//   Chip,
// } from "@mui/material";
// import SearchIcon from "@mui/icons-material/Search";
// import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
// import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
// import { useTheme } from "@mui/material/styles";

// import { useStyle } from "../../context/styleContext/styleContext";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
// import HomePageProductCard from "../../components/ProductCard/HomePageProductCard";
// import Seo from "../../components/Seo";
// import SaleHeroBanner from "../../components/SaleHeroBanner/SaleHeroBanner.jsx"

// function ProductListSchemaMarkup({ products }) {
//   useEffect(() => {
//     if (!products || products.length === 0) return;

//     const schemaData = {
//       "@context": "https://schema.org",
//       "@type": "CollectionPage",
//       name: "Parish Audio Products",
//       url: "https://parishaudio.com/products",
//       mainEntity: {
//         "@type": "ItemList",
//         itemListElement: products.map((product, index) => ({
//           "@type": "ListItem",
//           position: index + 1,
//           url: `https://parishaudio.com/products/${product.productURI}`,
//           name: product.name,
//         })),
//       },
//     };

//     const script = document.createElement("script");
//     script.type = "application/ld+json";
//     script.innerHTML = JSON.stringify(schemaData);
//     document.head.appendChild(script);

//     return () => {
//       document.head.removeChild(script);
//     };
//   }, [products]);

//   return null;
// }

// function inferProductGroup(product) {
//   const values = [
//     product?.category,
//     product?.productCategory,
//     product?.productType,
//     product?.type,
//     product?.group,
//     product?.name,
//     product?.shortDescription,
//     product?.cardSummary,
//   ]
//     .filter(Boolean)
//     .join(" ")
//     .toLowerCase();

//   if (
//     values.includes("plugin") ||
//     values.includes("vst") ||
//     values.includes("audio software") ||
//     values.includes("software")
//   ) {
//     return "plugins";
//   }

//   if (
//     values.includes("shirt") ||
//     values.includes("t-shirt") ||
//     values.includes("hoodie") ||
//     values.includes("merch") ||
//     values.includes("apparel")
//   ) {
//     return "merch";
//   }

//   return "other";
// }

// export default function ProductsPage() {
//   const [height, setHeight] = useState(window.innerHeight);
//   const [dataForAllProducts, setDataForAllProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [sortBy, setSortBy] = useState("featured");
//   const [groupFilter, setGroupFilter] = useState("all");

//   const { style } = useStyle();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   useEffect(() => {
//     window.scrollTo({ top: 0, behavior: "auto" });
//   }, []);

//   const getProductInfo = async () => {
//     try {
//       setLoading(true);
//       const { data } = await axios.post("/api/get-products-available-on-website");

//       if (data) {
//         setDataForAllProducts(data);
//       } else {
//         console.log("data error");
//       }
//     } catch (error) {
//       console.log("Error retrieving data...", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     getProductInfo();
//   }, []);

//   const filteredProducts = useMemo(() => {
//     let products = [...dataForAllProducts];

//     if (groupFilter !== "all") {
//       products = products.filter(
//         (product) => inferProductGroup(product) === groupFilter
//       );
//     }

//     const query = searchTerm.trim().toLowerCase();
//     if (query) {
//       products = products.filter((product) => {
//         const searchableText = [
//           product.name,
//           product.shortDescription,
//           product.cardSummary,
//           product.longDescription,
//           product.category,
//           product.productCategory,
//           product.productType,
//         ]
//           .filter(Boolean)
//           .join(" ")
//           .toLowerCase();

//         return searchableText.includes(query);
//       });
//     }

//     if (sortBy === "name-asc") {
//       products.sort((a, b) => a.name.localeCompare(b.name));
//     } else if (sortBy === "name-desc") {
//       products.sort((a, b) => b.name.localeCompare(a.name));
//     } else if (sortBy === "price-low") {
//       products.sort((a, b) => Number(a.price) - Number(b.price));
//     } else if (sortBy === "price-high") {
//       products.sort((a, b) => Number(b.price) - Number(a.price));
//     } else if (sortBy === "rating") {
//       products.sort((a, b) => {
//         const aRating = Number(a.starRating || 0);
//         const bRating = Number(b.starRating || 0);
//         const aCount = Number(a.ratingCount || 0);
//         const bCount = Number(a.ratingCount || 0);

//         if (bRating !== aRating) return bRating - aRating;
//         return bCount - aCount;
//       });
//     } else {
//       products.sort((a, b) => {
//         if (a.productURI === "d1-delay") return -1;
//         if (b.productURI === "d1-delay") return 1;
//         return 0;
//       });
//     }

//     return products;
//   }, [dataForAllProducts, searchTerm, sortBy, groupFilter]);

//   const categoryCounts = useMemo(() => {
//     return dataForAllProducts.reduce(
//       (acc, product) => {
//         const group = inferProductGroup(product);
//         acc.all += 1;
//         if (group === "plugins") acc.plugins += 1;
//         if (group === "merch") acc.merch += 1;
//         if (group === "other") acc.other += 1;
//         return acc;
//       },
//       { all: 0, plugins: 0, merch: 0, other: 0 }
//     );
//   }, [dataForAllProducts]);

//   return (
//     <>
//       <Seo
//         title="Products | Parish Audio"
//         description="Browse Parish Audio products. Professional tools and gear built for real-world music production."
//         canonical="https://parishaudio.com/products"
//         image="https://parishaudio.com/assets/HeroBanner1.png"
//         imageAlt="Parish Audio products"
//         ogType="website"
//       />

//       {filteredProducts.length > 0 && (
//         <ProductListSchemaMarkup products={filteredProducts} />
//       )}

//       <Box
//         sx={{
//           width: "100%",
//           maxWidth: "100vw",
//           overflowX: "hidden",
//           backgroundColor: "black",
//           px: { xs: 1.25, sm: 1.5, md: 2.5 },
//           py: { xs: 1.25, md: 2 },
//           boxSizing: "border-box",
//         }}
//       >
//         <Box
//           sx={{
//             width: "100%",
//             maxWidth: "1120px",
//             margin: "0 auto",
//             minHeight: height - 130,
//             display: "flex",
//             flexDirection: "column",
//             minWidth: 0,
//           }}
//         >
//           <SaleHeroBanner />
//           <br />
//           <Stack spacing={{ xs: 1.5, md: 2 }}>
//             <Paper
//               elevation={0}
//               sx={{
//                 width: "100%",
//                 minWidth: 0,
//                 borderRadius: "20px",
//                 overflow: "hidden",
//                 backgroundColor: style.color1,
//                 border: `1px solid ${style.color2}`,
//                 boxSizing: "border-box",
//               }}
//             >
//               <Box
//                 sx={{
//                   px: { xs: 1.25, sm: 1.5, md: 2 },
//                   pt: { xs: 1.25, md: 1.5 },
//                   pb: { xs: 1, md: 1.25 },
//                   backgroundColor: style.darkPanel,
//                   borderBottom: "1px solid rgba(255,255,255,0.08)",
//                 }}
//               >
//                 <Stack spacing={{ xs: 1.1, md: 1.25 }}>
//                   <Chip
//                     icon={<StorefrontRoundedIcon />}
//                     label="Store"
//                     sx={{
//                       alignSelf: "flex-start",
//                       borderRadius: "5px",
//                       fontWeight: 700,
//                       color: style.color4,
//                       backgroundColor: "rgba(255,255,255,0.06)",
//                       height: 28,
//                     }}
//                   />

//                   <Typography
//                     variant="h1"
//                     sx={{
//                       fontSize: { xs: "1.75rem", md: "2.3rem" },
//                       fontWeight: 800,
//                       lineHeight: 1.05,
//                       letterSpacing: "-0.03em",
//                       color: style.color4,
//                     }}
//                   >
//                     All Products
//                   </Typography>

//                   <Typography
//                     variant="body1"
//                     sx={{
//                       color: style.color3,
//                       maxWidth: "760px",
//                       fontSize: { xs: "0.95rem", md: "1rem" },
//                       lineHeight: 1.65,
//                     }}
//                   >
//                     Browse all plugins available from Parish Audio
//                   </Typography>
//                 </Stack>
//               </Box>

//               <Box
//                 sx={{
//                   px: { xs: 1, sm: 1.25, md: 1.5 },
//                   py: { xs: 1, md: 1.25 },
//                 }}
//               >
//                 <Paper
//                   elevation={0}
//                   sx={{
//                     backgroundColor: style.darkPanel,
//                     borderRadius: "14px",
//                     p: { xs: 1, md: 1.1 },
//                     border: "1px solid rgba(255,255,255,0.08)",
//                     boxSizing: "border-box",
//                     overflow: "hidden",
//                   }}
//                 >
//                   <Stack spacing={1.15}>
//                     {/* <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
//                       <Chip
//                         label={`All (${categoryCounts.all})`}
//                         onClick={() => setGroupFilter("all")}
//                         sx={filterChipStyle(style, groupFilter === "all")}
//                       />
//                       <Chip
//                         label={`Plugins (${categoryCounts.plugins})`}
//                         onClick={() => setGroupFilter("plugins")}
//                         sx={filterChipStyle(style, groupFilter === "plugins")}
//                       />
//                       <Chip
//                         label={`Merch (${categoryCounts.merch})`}
//                         onClick={() => setGroupFilter("merch")}
//                         sx={filterChipStyle(style, groupFilter === "merch")}
//                       />
//                     </Stack> */}

//                     <Stack
//                       direction={{ xs: "column", md: "row" }}
//                       spacing={1}
//                       sx={{ width: "100%", minWidth: 0 }}
//                     >
//                       <TextField
//                         fullWidth
//                         value={searchTerm}
//                         onChange={(e) => setSearchTerm(e.target.value)}
//                         placeholder="Search products..."
//                         variant="outlined"
//                         size="small"
//                         sx={{
//                           flex: 1.6,
//                           minWidth: 0,
//                           "& .MuiOutlinedInput-root": {
//                             color: style.color4,
//                             backgroundColor: "rgba(255,255,255,0.03)",
//                             borderRadius: "8px",
//                           },
//                           "& .MuiOutlinedInput-notchedOutline": {
//                             borderColor: style.color2,
//                           },
//                           "&:hover .MuiOutlinedInput-notchedOutline": {
//                             borderColor: style.color3,
//                           },
//                           "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
//                             borderColor: style.accentGreen,
//                           },
//                           "& .MuiInputBase-input::placeholder": {
//                             color: style.color3,
//                             opacity: 1,
//                           },
//                         }}
//                         InputProps={{
//                           startAdornment: (
//                             <InputAdornment position="start">
//                               <SearchIcon sx={{ color: style.color4 }} />
//                             </InputAdornment>
//                           ),
//                         }}
//                       />

//                       <FormControl
//                         size="small"
//                         sx={{
//                           minWidth: { xs: "100%", md: 220 },
//                           "& .MuiOutlinedInput-root": {
//                             color: style.color4,
//                             backgroundColor: "rgba(255,255,255,0.03)",
//                             borderRadius: "8px",
//                           },
//                           "& .MuiOutlinedInput-notchedOutline": {
//                             borderColor: style.color2,
//                           },
//                           "&:hover .MuiOutlinedInput-notchedOutline": {
//                             borderColor: style.color3,
//                           },
//                           "& .MuiSvgIcon-root": {
//                             color: style.color4,
//                           },
//                         }}
//                       >
//                         <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
//                         <Select
//                           value={sortBy}
//                           label="Sort"
//                           onChange={(e) => setSortBy(e.target.value)}
//                           startAdornment={
//                             !isMobile ? (
//                               <InputAdornment position="start">
//                                 <TuneRoundedIcon sx={{ color: style.color4, ml: 1 }} />
//                               </InputAdornment>
//                             ) : undefined
//                           }
//                         >
//                           <MenuItem value="featured">Featured</MenuItem>
//                           <MenuItem value="rating">Highest Rated</MenuItem>
//                           <MenuItem value="name-asc">Name: A to Z</MenuItem>
//                           <MenuItem value="name-desc">Name: Z to A</MenuItem>
//                           <MenuItem value="price-low">Price: Low to High</MenuItem>
//                           <MenuItem value="price-high">Price: High to Low</MenuItem>
//                         </Select>
//                       </FormControl>
//                     </Stack>
//                   </Stack>
//                 </Paper>

//                 <Typography
//                   variant="body2"
//                   sx={{
//                     color: style.color3,
//                     mt: 1,
//                     mb: 0,
//                     ml: 0.25,
//                     fontSize: "0.9rem",
//                   }}
//                 >
//                   {loading
//                     ? "Loading products..."
//                     : `${filteredProducts.length} product${filteredProducts.length === 1 ? "" : "s"}`}
//                 </Typography>
//               </Box>
//             </Paper>

//             <Paper
//               elevation={0}
//               sx={{
//                 width: "100%",
//                 minWidth: 0,
//                 borderRadius: "20px",
//                 overflow: "hidden",
//                 backgroundColor: style.color1,
//                 border: `1px solid ${style.color2}`,
//                 boxSizing: "border-box",
//               }}
//             >
//               <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
//                 {loading ? (
//                   <Box sx={{ py: 6 }}>
//                     <LoadingSpinner />
//                   </Box>
//                 ) : filteredProducts.length === 0 ? (
//                   <Box
//                     sx={{
//                       py: 6,
//                       textAlign: "center",
//                       color: style.color4,
//                     }}
//                   >
//                     <Typography variant="h6" sx={{ mb: 1, fontWeight: 800 }}>
//                       No products found
//                     </Typography>
//                     <Typography variant="body2" sx={{ color: style.color3 }}>
//                       Try a different search term or category.
//                     </Typography>
//                   </Box>
//                 ) : (
//                   <Box
//                     sx={{
//                       display: "grid",
//                       gridTemplateColumns: {
//                         xs: "1fr",
//                         sm: "repeat(2, minmax(0, 1fr))",
//                         md: "repeat(3, minmax(0, 1fr))",
//                         lg: "repeat(4, minmax(0, 1fr))",
//                       },
//                       gap: 2,
//                       alignItems: "stretch",
//                     }}
//                   >
//                     {filteredProducts.map((product, index) => (
//                       <Box
//                         key={product.SKU || product.productURI || index}
//                         sx={{
//                           minWidth: 0,
//                           width: "100%",
//                           display: "flex",
//                         }}
//                       >
//                         <Box
//                           sx={{
//                             width: "100%",
//                             minWidth: 0,
//                             display: "flex",
//                             marginBottom: 4,
//                           }}
//                         >
//                           <HomePageProductCard productData={product} />
//                         </Box>
//                       </Box>
//                     ))}
//                   </Box>
//                 )}
//               </Box>
//             </Paper>
//           </Stack>
//         </Box>
//       </Box>
//     </>
//   );
// }

// function filterChipStyle(style, active) {
//   return {
//     borderRadius: "5px",
//     fontWeight: 700,
//     color: style.color4,
//     backgroundColor: active ? style.accentGreen : "rgba(255,255,255,0.06)",
//     border: active
//       ? "1px solid rgba(255,255,255,0.10)"
//       : "1px solid rgba(255,255,255,0.06)",
//     "&:hover": {
//       backgroundColor: active
//         ? style.accentGreen
//         : "rgba(255,255,255,0.10)",
//     },
//   };
// }